import React, { FC } from 'react';

import Check from '@/assets/check.svg?react';
import cn from '@/utils/cn';

interface Props {
  caption: string;
  title: string;
  steps: Step[];
  className?: string;
  stepsInfo?: React.ReactNode;
}

const Steps: FC<Props> = ({
  caption = '3 easy steps',
  title = 'Get started with us',
  steps,
  className,
  stepsInfo,
}) => {
  return (
    <div className={cn('py-[42px] lg:p-10', className)}>
      <div className="flex flex-col gap-y-3 pb-6">
        <p className="input-label text-blue-600 ">{caption}</p>
        <h3 className="h2">{title}</h3>
        {stepsInfo}
      </div>
      <div className="flex flex-col gap-y-5 lg:gap-[28px]">
        {steps.map((step, index) => (
          <Step
            key={index}
            title={step.title}
            active={step.active}
            description={step.description}
            withMarginTop={index === 0}
            withoutLine={index === steps.length - 1}
            lineActive={step.active && steps[index + 1]?.active}
          />
        ))}
      </div>
    </div>
  );
};

export default Steps;

interface Step {
  active: boolean;
  title: string;
  description: string | React.ReactNode;
  withMarginTop?: boolean;
  withoutLine?: boolean;
  isActive?: boolean;
  lineActive?: boolean;
}

const Step: FC<Step> = ({
  title,
  description,
  active,
  withMarginTop,
  withoutLine,
  lineActive,
}) => (
  <div className="relative flex">
    {!withoutLine && (
      <div
        className={cn(
          'absolute left-[11px] top-[6px] h-[calc(100%+40px)] w-[1px] -translate-x-1/2 bg-dark-gray-100',
          withMarginTop && 'lg:top-2',
          lineActive && 'bg-blue-600'
        )}
      />
    )}
    <div className="z-20 flex flex-col gap-1">
      <p className="h4 lg:h5 flex items-center gap-3 text-dark-gray-500 lg:gap-4">
        <span
          className={cn(
            'flex size-[22px] shrink-0 items-center justify-center rounded-full bg-dark-gray-100',
            active && 'bg-blue-600',
            withMarginTop && 'translate-y-1.5'
          )}
        >
          {active && <Check className="text-gray-100" width={14} />}
        </span>
        {title}
      </p>
      <div className="body-small ml-[34px] text-dark-gray-400 lg:ml-[38px]">
        {description}
      </div>
    </div>
  </div>
);
